import React, { FC, useEffect, useMemo, useState } from 'react'
import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { Form, useNavigate, useParams } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import './add-new-card.css'

// COMPONENTS
import { BillingAddressFields } from './billing-address'
import CharacterCount from '@components/atoms/character-count'
import ErrorModal from '@components/molecules/error-modal'
import TextField from '@components/atoms/textfield'

// TYPES
import type { FormProps } from 'react-router-dom'
import type { Address, PaymentMethod, SetupIntent, StripeElementStyle, StripeError } from '@stripe/stripe-js'

// UTILITIES
import { StripeAPI } from '@apis'
import { flatten } from '@utils/string-helper'
import { useInput } from '@hooks/inputs'


type OnAddressChangeParams = Parameters<NonNullable<React.ComponentProps<typeof BillingAddressFields>['onAddressChange']>>
interface Props extends FormProps {
    goBackCount?: number
    onValidCard: (isValid: boolean) => void
    onValidBillingAddress: (isValid: boolean) => void
    onCardCreated: (setupIntent: SetupIntent, paymentMethod: PaymentMethod) => void
    onCardError: (error: StripeError) => void
}

const cardNickNameMax = 25

const AddNewCard: FC<Props> = (props) => {
    const { className: classProps, goBackCount, onSubmit: onSubmitProps, onValidCard, onCardCreated, onCardError, onValidBillingAddress, ...formProps } = props
    const { id: userId } = useUser().user || {}
    const { groupId } = useParams()
    const navigate = useNavigate()
    const elements = useElements()
    const stripe = useStripe()

    const className = flatten('add-new-card-container', 'flex flex-col')
    const [isLoading, setIsLoading] = useState(false)
    const [isBilling, setIsBilling] = useState(false)

    const [cardName, cardNameProps] = useInput()
    const [validCardNumber, setValidCardNumber] = useState(false)
    const [validExpiry, setValidExpiry] = useState(false)
    const [validCvc, setValidCvc] = useState(false)
    const [cardNickName, cardNickNameProps] = useInput()

    const [billingAddress, setBillingAddress] = useState<{ address?: Address, valid?: boolean }>({})
    const validCard = useMemo(() => (
        (!!cardName && cardName.includes(' '))
        && validCardNumber && validExpiry && validCvc
    ), [cardName, validCardNumber, validExpiry, validCvc])

    const [showError, setError] = useState(false)
    const [error, setErrorData] = useState<StripeError>()

    useEffect(() => {
        if (onValidCard) onValidCard(validCard)
    }, [validCard])

    useEffect(() => {
        if (onValidBillingAddress) onValidBillingAddress(!!billingAddress.valid)
    }, [billingAddress.valid])

    useEffect(() => {
        if (!!goBackCount) swap()
    }, [goBackCount])

    async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        if (isLoading) return e.preventDefault();
        if (onSubmitProps) onSubmitProps(e);
        if (!e.defaultPrevented) e.preventDefault();

        if (validCard && (!billingAddress.valid || !isBilling)) {
            swap()
            return;
        } else setIsLoading(true)

        if (!stripe || !elements) throw new Error("Stripe has yet to load");

        const cardElement = elements.getElement('cardNumber')
        if (!cardElement || !userId) throw new Error(`Missing Data ${JSON.stringify({ cardElement: cardElement == null, groupId, userId })}`)

        const { clientSecret } = await StripeAPI.CreateSetupIntentRequest.fetch()
        const _address = billingAddress.address as any
        if (!!_address) delete _address['email']

        const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
            type: 'card',
            card: cardElement,
            billing_details: {
                name: cardName || undefined,
                email: billingAddress.address?.email || undefined,
                phone: undefined,
                address: billingAddress.address,
            },
            metadata: {
                groupId: groupId || null,
                nickname: cardNickName || null
            },
        })
        if (!!pmError || !paymentMethod) {
            setErrorData(pmError)
            setError(true)
            setIsLoading(false)
            onCardError(pmError)
            throw new Error("Failed to generate payment method")
        }

        const { setupIntent, error } = await stripe.confirmCardSetup(clientSecret, {
            payment_method: paymentMethod.id
        })
        if (!!error || !setupIntent) {
            setErrorData(error)
            setError(true)
            setIsLoading(false)
            onCardError(error)
            throw error || new Error("Failed to retain payment method")
        }

        setIsLoading(false)
        onCardCreated(setupIntent, paymentMethod)
    }

    function handleAddressChange(...[address, valid]: OnAddressChangeParams) {
        setBillingAddress({ address, valid })
    }

    // ToDo: make better
    function swap() {
        const cardDetails = document.querySelector('fieldset[name="card-details"]')
        const billingAddress = document.querySelector('fieldset[name="billing-address"]')
        if (!!cardDetails && billingAddress) {
            if (cardDetails.getAttribute("data-state") === 'active') {
                setIsBilling(true)
                cardDetails.setAttribute("data-state", "fade-out")
                setTimeout(() => {
                    cardDetails.setAttribute("data-state", "hidden")
                    billingAddress.removeAttribute("disabled")
                    billingAddress.setAttribute("data-state", "fade-in")
                    setTimeout(() => {
                        billingAddress.setAttribute("data-state", "active")
                    }, 200)
                }, 200)
            } else if (billingAddress.getAttribute("data-state") === 'active') {
                setIsBilling(false)
                billingAddress.setAttribute("data-state", "fade-out")
                setTimeout(() => {
                    billingAddress.setAttribute("data-state", "hidden")
                    billingAddress.setAttribute("disabled", "true")
                    cardDetails.setAttribute("data-state", "fade-in")
                    setTimeout(() => {
                        cardDetails.setAttribute("data-state", "active")
                    }, 200)
                }, 200)
            }
        }
    }

    return (<>
        <Form className={className} onSubmit={handleSubmit} {...formProps}>
            <fieldset name="card-details" data-state="active">
                <TextField {...cardNameProps} name="name" placeholder="Name on Card" autoComplete="cc-name" required />
                <CardNumberElement className="textfield" options={{ showIcon: true, placeholder: 'Card Number', style: baseStyles }} onChange={({ complete }) => {
                    setValidCardNumber(complete)
                }} />
                <div className="input-row">
                    <CardExpiryElement className="textfield" options={{ style: baseStyles }} onChange={({ complete }) => { setValidExpiry(complete) }} />
                    <CardCvcElement className="textfield" options={{ style: baseStyles }} onChange={({ complete }) => { setValidCvc(complete) }} />
                </div>
                <TextField {...cardNickNameProps} name="nick-name" maxLength={cardNickNameMax} autoComplete="off" placeholder="Card Nickname (optional)" inlineElementRight={<CharacterCount label={cardNickName} max={cardNickNameMax} />} />
            </fieldset>
            <BillingAddressFields initAddress={billingAddress.address} onAddressChange={handleAddressChange} name="billing-address" data-state="hidden" disabled />
        </Form>
        <ErrorModal
            state={[showError, setError]}
            error={error}
            title="Error"
            fallbackMessage="We encountered an issue while adding your payment method. Please try again!"
            confirmLabel="Got it!"
            onClose={() => navigate(-1)}
        />
    </>)
}

const baseStyles: StripeElementStyle = {
    base: {
        // fontSize: "clamp(14px, 3.75vw, 16px)",
        // fontSize: "clamp(14px, 11.875vw, 16px)",
        fontFamily: "Open Sans, sans-serif",
        // lineHeight: "clamp(17.6px, 3.75vw, 24px)",
        color: "black",
        "::placeholder": {
            color: "rgba(119, 121, 134, 0.8)" // var(--splitcard-gray-500)
        }
    }
}

export { AddNewCard }
export { BillingAddressFields }
